import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import type {
  PriorityControl,
  PriorityState,
  PriorityTap,
  PriorityValue,
} from '@/lib/audition/job-priorities'
import {
  askedPriorities,
  detailHint,
  PRIORITY_COPY,
  placedPriorities,
  prioritiesReadback,
  priorityBoard,
  priorityFollowUps,
  priorityLabel,
  tapPriority,
  withFollowUps,
} from '@/lib/audition/job-priorities'
import { cn } from '@/lib/utils'
import { FollowUpCard } from './priority-followups'

/** Which half of the question is on screen. Interaction, never an answer. */
type Phase = 'rank' | 'detail'

/**
 * "What are you actually looking for?" — eight cards ranked over a pile of
 * the ones that do not matter here.
 *
 * Explicit move controls rather than a drag gesture. The option boards tried
 * dragging and rejected it: a phone that cannot collect this is a phone that
 * disqualifies people, and a ranking is exactly the interaction a drag makes
 * hardest on a small screen with a thumb.
 *
 * Two phases, not two questions: the ladder settles the order, then phase two
 * asks what the three that ended on top actually mean. They are split because
 * the ranking is what chooses the follow-ups — and because the whole eight
 * with every pile open underneath is the page the design critique scored
 * 20/40. One at a time is the load budget being spent deliberately.
 *
 * Every rule and every sentence is in the job-priorities module and the
 * priority-followups module beside it. This file draws what they say and
 * reports taps back.
 */
export function JobPriorities({
  label,
  value,
  onChange,
  disabled,
  describedBy,
  invalid,
}: {
  label: string
  value: PriorityValue
  onChange: (value: PriorityValue) => void
  disabled?: boolean
  /** Id of help text describing this group, hung on the group itself. */
  describedBy?: string
  /** Whether this group is why the step was refused. */
  invalid?: boolean
}) {
  const board = priorityBoard(value)
  // Scoped per instance so two ladders on one page cannot both claim the
  // same description id and point aria-describedby at the wrong card.
  const uid = useId()

  // The half of the interaction that is not an answer: the memory of where a
  // card was when it left the ladder, and where focus belongs after the edit
  // that just committed. Both are the module's to decide — `tapPriority`
  // returns them with the value — and neither is ever stored.
  //
  // Focus has to be handed over at all because dropping a card unmounts the
  // button that was focused, and a widget whose whole premise is repeated
  // small edits cannot dump a keyboard user at the top of the document after
  // each one.
  const [memory, setMemory] = useState<Omit<PriorityState, 'value'>>({
    droppedFrom: {},
    focus: null,
  })
  // Which phase is showing is interaction, not an answer, so it lives here
  // and resets when the step is left. Resuming a draft onto phase two would
  // open on questions chosen by a ranking the applicant cannot see.
  const [phase, setPhase] = useState<Phase>('rank')
  // Where focus goes after a phase switch, and nothing on a first mount.
  // Both controls UNMOUNT themselves by switching the phase, which drops
  // focus onto <body>: the next Tab restarts at the top of the document and
  // a screen reader says nothing about the whole question having been
  // replaced. Same handover the card moves already do, one view up.
  const [arriving, setArriving] = useState<Phase | null>(null)
  const detailHeadingRef = useRef<HTMLParagraphElement>(null)
  const toDetailRef = useRef<HTMLButtonElement>(null)
  const leavePhase = (next: Phase) => {
    setPhase(next)
    setArriving(next)
  }
  useEffect(() => {
    if (arriving === null) return
    // Into phase two, the heading that replaced the ladder; back out of it,
    // the button they pressed to get there, which is where they left off.
    if (arriving === 'detail') detailHeadingRef.current?.focus()
    else toDetailRef.current?.focus()
    setArriving(null)
  }, [arriving])
  // One node per card per control, so nothing has to compose a key. The
  // string template this replaces was written at one site and read at four,
  // and none of the five checked the other.
  const nodes = useRef<
    Record<string, Partial<Record<PriorityControl, HTMLButtonElement | null>>>
  >({})
  const keepNode = (
    id: string,
    control: PriorityControl,
    node: HTMLButtonElement | null,
  ) => {
    const card = (nodes.current[id] ??= {})
    card[control] = node
  }

  const { focus } = memory
  useEffect(() => {
    if (focus === null) return
    nodes.current[focus.id]?.[focus.control]?.focus()
    // Cleared only if it is still the target this effect moved to. Two taps
    // inside one passive-effect window — a held key, or a fast double tap —
    // run this once with the newer target while the older one is still
    // queued; clearing unconditionally would wipe the target nobody has moved
    // to yet and leave the applicant's focus on <body>, halfway down a list
    // they were reordering by keyboard.
    setMemory((m) => (m.focus === focus ? { ...m, focus: null } : m))
  }, [focus])

  // Built once per render so a refused tap can be recognised by identity:
  // `tapPriority` hands back the state it was given when nothing may change,
  // and a refused tap must save nothing AND move nothing.
  const state: PriorityState = { value, ...memory }
  const tap = (action: PriorityTap) => {
    const next = tapPriority(state, action)
    if (next === state) return
    setMemory({ droppedFrom: next.droppedFrom, focus: next.focus })
    onChange(next.value)
  }

  // The three attributes that name and qualify the list, in one object purely
  // so the <ol> below fits on the single line its biome-ignore is attached to
  // — split across lines, the suppression stops covering `role="list"`.
  const naming = {
    'aria-label': label,
    'aria-describedby': describedBy,
    'aria-invalid': invalid || undefined,
  }

  const followUps = priorityFollowUps(value)
  // Only the tops that actually ask something — a range or a pile. A
  // priority with neither would draw a bordered box with a heading and
  // nothing in it; `askedPriorities` carries the reasoning.
  const asked = askedPriorities(value)

  if (phase === 'detail')
    return (
      // The same three attributes the ladder carries. Without them phase two
      // has no accessible name at all — `QuestionStep` renders this widget's
      // heading as plain text precisely because the widget is supposed to
      // name itself — and a refused Next has nothing to point at.
      <div role="group" {...naming} className="space-y-4">
        <div className="space-y-1">
          <p
            ref={detailHeadingRef}
            // Focused when the phase switches, never in the tab order.
            tabIndex={-1}
            className="text-sm font-semibold text-foreground outline-none"
          >
            {PRIORITY_COPY.detailHeading}
          </p>
          {/* Names them and says the ranking chose them. Without it a pile
              that vanished after a demotion looks like a bug. */}
          <p className="text-sm text-muted-foreground text-pretty">
            {detailHint(value)}
          </p>
        </div>

        {asked.map(({ id, rank }) => (
          <FollowUpCard
            key={id}
            priorityId={id}
            rank={rank}
            answers={followUps}
            // Written onto the DRAWN board, exactly as a ladder tap is.
            // Answering phase two means they read that order and let it
            // stand, so it is their ranking now — written off the stored
            // value instead, an applicant who accepts our order stores two
            // empty piles and the admin recap prints nothing at all.
            onChange={(next) =>
              onChange(withFollowUps(placedPriorities(board, value), next))
            }
            disabled={disabled}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => leavePhase('rank')}
        >
          <ArrowLeft aria-hidden="true" />
          {PRIORITY_COPY.toRank}
        </Button>
      </div>
    )

  return (
    <div className="space-y-4">
      {/* `role="list"` said out loud, because `list-style: none` makes WebKit
          strip the implicit list and listitem roles — and this <ol> is the
          only thing carrying the question's name, so without it VoiceOver
          reaches eight loose buttons, no question, and no "2 of 6". Position
          IS the answer here: the visible rank number is aria-hidden. */}
      {/* biome-ignore lint/a11y/noRedundantRoles: not redundant — WebKit drops the implicit list role when list-style is none, and this <ol> is the widget's only accessible name */}
      <ol role="list" {...naming} className="list-none space-y-2 p-0">
        {board.ranked.map((id, index) => (
          <Card
            key={id}
            uid={uid}
            id={id}
            rank={index + 1}
            total={board.ranked.length}
            top={board.top.includes(id)}
            first={index === 0}
            last={index === board.ranked.length - 1}
            disabled={!!disabled}
            refFor={(control, node) => keepNode(id, control, node)}
            onUp={() => tap({ kind: 'move', id, delta: -1 })}
            onDown={() => tap({ kind: 'move', id, delta: 1 })}
            onDrop={() => tap({ kind: 'drop', id })}
          />
        ))}
      </ol>

      {board.ranked.length === 0 && (
        <p className="rounded-xl border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          {PRIORITY_COPY.emptyLadder}
        </p>
      )}

      {board.dropped.length > 0 && (
        // Its own named group, and the instruction is its own element: as a
        // bare span glued to the end of the heading it read aloud as one
        // run-on sentence.
        <div role="group" aria-labelledby={`${uid}-pile`} className="space-y-2">
          <p
            id={`${uid}-pile`}
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {PRIORITY_COPY.pileHeading}
          </p>
          <p className="text-xs text-muted-foreground">
            {PRIORITY_COPY.pileHint}
          </p>
          <div className="flex flex-wrap gap-2">
            {board.dropped.map((id) => (
              <button
                key={id}
                ref={(node) => {
                  keepNode(id, 'add', node)
                }}
                type="button"
                // Named with its verb, like every control on a ranked card.
                // The label alone tells a screen-reader user what the chip
                // IS, never what pressing it does — and this is the only way
                // back from a mis-tap on the remove button.
                aria-label={`Add ${priorityLabel(id)} to your list`}
                disabled={disabled}
                onClick={() => tap({ kind: 'restore', id })}
                className={cn(
                  'inline-flex min-h-11 items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors',
                  'outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                  'hover:bg-accent/50 disabled:pointer-events-none disabled:opacity-60',
                )}
              >
                <Plus aria-hidden="true" className="size-3.5" />
                {priorityLabel(id)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* The plain-English readback. A ranking without one is a puzzle. */}
      <p aria-live="polite" className="text-sm text-muted-foreground">
        {prioritiesReadback(value)}
      </p>

      {/* Said before they commit to an order, not after: discovering that the
          ranking chose the next questions only once you are looking at them
          is what makes a form feel like it was not listening. */}
      <p className="text-sm text-muted-foreground text-pretty">
        {PRIORITY_COPY.rankHint}
      </p>

      <Button
        type="button"
        variant="outline"
        ref={toDetailRef}
        // Nothing to ask means phase two is an empty page, and a button onto
        // one is worse than no button. Asked of the piles rather than of the
        // ladder: a full ladder whose top three all declare no groups has the
        // same nothing behind the button as an empty one.
        disabled={disabled || asked.length === 0}
        onClick={() => leavePhase('detail')}
      >
        {PRIORITY_COPY.toDetail}
        <ArrowRight aria-hidden="true" />
      </Button>
    </div>
  )
}

function Card({
  uid,
  id,
  rank,
  total,
  top,
  first,
  last,
  disabled,
  refFor,
  onUp,
  onDown,
  onDrop,
}: {
  uid: string
  id: string
  rank: number
  total: number
  top: boolean
  first: boolean
  last: boolean
  disabled: boolean
  refFor: (control: PriorityControl, node: HTMLButtonElement | null) => void
  onUp: () => void
  onDown: () => void
  onDrop: () => void
}) {
  const label = priorityLabel(id)
  const nameId = `${uid}-${id}-name`
  const posId = `${uid}-${id}-pos`

  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-xl border py-2 pl-3.5 pr-2',
        // The top three carry the answer, so they are the three that look
        // like it — and the only three the follow-up step will ask about.
        top
          ? 'border-border bg-background shadow-sm'
          : 'border-input bg-muted/40',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'w-5 shrink-0 text-sm font-semibold tabular-nums',
          top ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {rank}
      </span>
      <span
        id={nameId}
        className={cn(
          'min-w-0 flex-1 text-sm text-pretty',
          top ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      {/* The rank is the answer, and the number beside it is a drawing. This
          is the only place a screen reader hears the position — and because
          it rides on every control's description, refocusing a card after a
          move announces where the move put it. Without it, moving a card
          below the top three produced no feedback at all: the readback names
          only the top three, so its text never changed. */}
      <span id={posId} className="sr-only">
        {rank} of {total}
      </span>
      <span className="flex shrink-0 items-center">
        <Nudge
          ref={(node) => {
            refFor('up', node)
          }}
          describedBy={`${nameId} ${posId}`}
          name={`Move ${label} up`}
          // The end cards keep their buttons focusable and explain
          // themselves through position rather than vanishing: a control
          // that disappears at the edge of a list is a control the applicant
          // has to re-find every time they move a card.
          closed={first}
          disabled={disabled}
          onClick={onUp}
        >
          <ChevronUp aria-hidden="true" className="size-4" />
        </Nudge>
        <Nudge
          ref={(node) => {
            refFor('down', node)
          }}
          describedBy={`${nameId} ${posId}`}
          name={`Move ${label} down`}
          closed={last}
          disabled={disabled}
          onClick={onDown}
        >
          <ChevronDown aria-hidden="true" className="size-4" />
        </Nudge>
        {/* Set apart from the two chevrons by a divider and a real gap. This
            widget exists because dragging is unusable with a thumb, and three
            44px targets in an unbroken strip — the third destructive, and its
            only undo puts the card back at the BOTTOM — trades one bad
            gesture for another. */}
        <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border" />
        <Nudge
          ref={(node) => {
            refFor('drop', node)
          }}
          describedBy={`${nameId} ${posId}`}
          name={`Take ${label} off the list`}
          closed={false}
          disabled={disabled}
          onClick={onDrop}
        >
          <X aria-hidden="true" className="size-3.5" />
        </Nudge>
      </span>
    </li>
  )
}

function Nudge({
  ref,
  describedBy,
  name,
  closed,
  disabled,
  onClick,
  children,
}: {
  ref?: (node: HTMLButtonElement | null) => void
  describedBy: string
  name: string
  /** True when this direction has nowhere to go from where the card sits. */
  closed: boolean
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={name}
      aria-describedby={describedBy}
      // Focusable but inert, matching the level ladder: `disabled` would drop
      // it out of the tab order mid-list, so a keyboard user tabbing down the
      // ranking would find the stops moving under them as cards move.
      aria-disabled={closed || undefined}
      disabled={disabled}
      onClick={closed ? undefined : onClick}
      className={cn(
        // A real 44px box, not a 32px one with its hit area grown by a
        // pseudo-element: grown areas overlap their neighbours and the later
        // sibling wins the tap, which on a row ending in a destructive
        // control is the mis-tap this is meant to prevent. 44px is the whole
        // reason this widget is taps rather than a drag.
        'flex size-11 items-center justify-center rounded-md text-muted-foreground transition-colors',
        'outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50',
        closed ? 'opacity-30' : 'hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
